import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ChangeEmailDialog } from '@/components/settings/ChangeEmailDialog';
import { ChangePasswordDialog } from '@/components/settings/ChangePasswordDialog';
import { NotificationPreferences } from '@/components/settings/NotificationPreferences';
import { PaymentMethodManager } from '@/components/payments/PaymentMethodManager';
import { StripeConnectManager } from '@/components/payments/StripeConnectManager';
import { MFAManager } from '@/components/settings/MFAManager';
import { useOnboardingTour } from '@/hooks/useOnboardingTour';
import { Mail, Shield, Lock, PlayCircle } from 'lucide-react';

export default function AccountSettingsPage() {
  const { user } = useAuth();
  const { startTour } = useOnboardingTour();
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [organizationId, setOrganizationId] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrganizationId = async () => {
      if (!user?.id) return;
      
      const { data } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();
      
      if (data?.organization_id) {
        setOrganizationId(data.organization_id);
      }
    };

    fetchOrganizationId();
  }, [user?.id]);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Account Settings</h1>
        <p className="text-muted-foreground mt-2">
          Manage your account security and authentication settings
        </p>
      </div>

      {/* Onboarding Tour - Show for clients and vendors */}
      {(user?.role === 'client' || user?.role === 'vendor') && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PlayCircle className="h-5 w-5" />
              Onboarding Tour
            </CardTitle>
            <CardDescription>
              Replay the interactive tour to learn about platform features
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-secondary rounded-lg">
              <div className="flex-1">
                <p className="text-sm text-muted-foreground">Interactive Platform Tour</p>
                <p className="text-base font-medium text-foreground">Step-by-step guide through all features</p>
              </div>
              <Button onClick={startTour} variant="outline">
                Replay Tour
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Address
          </CardTitle>
          <CardDescription>
            Your email is used for login and important notifications
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-secondary rounded-lg">
            <div className="flex-1">
              <p className="text-sm text-muted-foreground">Current Email</p>
              <p className="text-base font-medium text-foreground">{user?.email}</p>
              {user && (
                <div className="flex items-center gap-2 mt-2">
                  <div className={`h-2 w-2 rounded-full ${user.email_confirmed_at ? 'bg-green-500' : 'bg-yellow-500'}`} />
                  <p className="text-xs text-muted-foreground">
                    {user.email_confirmed_at ? 'Verified' : 'Pending verification'}
                  </p>
                </div>
              )}
            </div>
            <Button onClick={() => setShowEmailDialog(true)} variant="outline">
              Change Email
            </Button>
          </div>

          <Alert>
            <Shield className="h-4 w-4" />
            <AlertDescription>
              Changing your email requires verification. You'll receive a confirmation link at your new email address.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Password Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Password
          </CardTitle>
          <CardDescription>
            Change your password to keep your account secure
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-secondary rounded-lg">
            <div className="flex-1">
              <p className="text-sm text-muted-foreground">Password</p>
              <p className="text-base font-medium text-foreground">••••••••••</p>
            </div>
            <Button onClick={() => setShowPasswordDialog(true)} variant="outline">
              Change Password
            </Button>
          </div>

          <Alert>
            <Shield className="h-4 w-4" />
            <AlertDescription>
              Choose a strong password with at least 6 characters. Changing your password will log you out of other devices.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Two-Factor Authentication Section */}
      <MFAManager />

      {/* Payment Setup - Role-specific */}
      {organizationId && user?.role === 'client' && <PaymentMethodManager organizationId={organizationId} />}
      {organizationId && user?.role === 'vendor' && <StripeConnectManager organizationId={organizationId} />}

      {/* Notification Preferences */}
      <NotificationPreferences />

      <ChangeEmailDialog
        open={showEmailDialog} 
        onOpenChange={setShowEmailDialog}
      />
      
      <ChangePasswordDialog 
        open={showPasswordDialog} 
        onOpenChange={setShowPasswordDialog}
      />
    </div>
  );
}
